<style lang="sass" scoped>
h1
    @apply font-bold text-4xl text-[#111827]
h2
    @apply font-bold text-2xl text-[#111827]

main, footer
    @apply max-w-[1280px] mx-auto my-12

.autocomplete-item
    @apply flex items-center p-2 px-4 border hover:border-gray-700 hover:text-primary-600 cursor-pointer mb-1
</style>

<script setup lang="ts">
import { Form, type FormResolverOptions } from '@primevue/forms'
import type { FormSubmitEvent } from '@primevue/forms/form'
import Button from 'primevue/button'
import Checkbox from 'primevue/checkbox'
import DatePicker from 'primevue/datepicker'
import Divider from 'primevue/divider'
import Drawer from 'primevue/drawer'
import FloatLabel from 'primevue/floatlabel'
import IconField from 'primevue/iconfield'
import IftaLabel from 'primevue/iftalabel'
import Image from 'primevue/image'
import InputGroup from 'primevue/inputgroup'
import InputGroupAddon from 'primevue/inputgroupaddon'
import InputIcon from 'primevue/inputicon'
import InputNumber from 'primevue/inputnumber'
import InputText from 'primevue/inputtext'
import Message from 'primevue/message'
import Panel from 'primevue/panel'
import Popover from 'primevue/popover'
import ScrollPanel from 'primevue/scrollpanel'
import Tag from 'primevue/tag'
import Textarea from 'primevue/textarea'
import Toast from 'primevue/toast'
import VirtualScroller from 'primevue/virtualscroller'

import allItemsDataJson from './items.json'
import rawRoomsData from './rooms.json'

import { useToast } from 'primevue/usetoast'
import { v4 as uuidv4 } from 'uuid'
import { usePlacesAutocomplete } from 'vue-use-places-autocomplete'
import { app, type appT } from './store'
import type {
    AskForm,
    Cargo,
    Company,
    EmailShareForm,
    ItemDefinition,
    OrderNowForm,
    Room,
    TInput,
    TInputField,
    ValidationError
} from './types'

const { t, locale, availableLocales } = useI18n({ useScope: 'global' })

const apps: appT = app()
const uuid = (): string => uuidv4()
const toast = useToast()
const { width } = useWindowSize()

// rooms

const selectionModeActive = ref<boolean>(false)
const availableRooms = ref<Room[]>([])
const selectedRoomIds = ref<string[]>([])

const selectionStep = ref<number>(1)
const allItemsMap = ref<Map<string, ItemDefinition>>(new Map())
const searchQuery = ref<string>('')
const itemQuantities = reactive<Record<string, number>>({})

//

const units = ref<string>('cm')

const defaultValue = computed(() => {
    switch (apps.activeCargo) {
        case 'pallet':
            return {
                cbm: null,
                wgt: 300,
                lth: units.value === 'm' ? 1.2 : 120,
                wth: units.value === 'm' ? 0.8 : 80,
                hgt: units.value === 'm' ? 1.5 : 150
            }

        case 'van':
            return {
                cbm: null,
                wgt: 1200,
                lth: units.value === 'm' ? 3.7 : 370,
                wth: units.value === 'm' ? 1.8 : 180,
                hgt: units.value === 'm' ? 1.9 : 190
            }

        case 'moving':
            return {
                cbm: 1,
                wgt: null,
                lth: null,
                wth: null,
                hgt: null
            }
    }

    return { cbm: null, wgt: null, lth: null, wth: null, hgt: null }
})

const cargo = ref<string | null>(null)
const data = reactive<TInput[]>([])

const cargos = computed(
    (): Cargo => ({
        docs: { label: t('cargos.docs'), icon: 'pi pi-file', disabled: true },
        box: { label: t('cargos.box'), icon: 'pi pi-box', disabled: true },
        pallet: { label: t('cargos.pallet'), icon: 'pi pi-warehouse', disabled: false },
        van: { label: t('cargos.van'), icon: 'pi pi-car', disabled: false },
        truck: { label: t('cargos.truck'), icon: 'pi pi-truck', disabled: true },
        container: { label: t('cargos.container'), icon: 'pi pi-box', disabled: true },
        moving: { label: t('cargos.moving'), icon: 'pi pi-building', disabled: false }
    })
)

const init = (): void => {
    data.length = 0
    if (apps.activeCargo === 'moving') {
        data.push({
            uuid: uuid(),
            qty: { label: 'quantity', value: 1, suffix: '' },
            cbm: { label: 'cbm', value: defaultValue.value.cbm, suffix: ' m³' }
        })
    } else {
        data.push({
            uuid: uuid(),
            qty: { label: 'quantity', value: 1, suffix: '' },
            wgt: { label: 'weightKg', value: defaultValue.value.wgt, suffix: ' kg' },
            lth: { label: 'length', value: defaultValue.value.lth, suffix: '' },
            wth: { label: 'width', value: defaultValue.value.wth, suffix: '' },
            hgt: { label: 'height', value: defaultValue.value.hgt, suffix: '' }
        })
    }
}

const onClickAddMoreButton = (_i: number): void => {
    if (apps.activeCargo === 'moving') {
        data.push({
            uuid: uuid(),
            qty: { label: 'quantity', value: 1, suffix: '' },
            cbm: { label: 'cbm', value: defaultValue.value.cbm, suffix: ' m³' }
        })
    } else {
        data.splice(_i + 1, 0, {
            uuid: uuid(),
            qty: { label: 'quantity', value: 1, suffix: '' },
            wgt: { label: 'weightKg', value: defaultValue.value.wgt, suffix: ' kg' },
            lth: { label: 'length', value: defaultValue.value.lth, suffix: '' },
            wth: { label: 'width', value: defaultValue.value.wth, suffix: '' },
            hgt: { label: 'height', value: defaultValue.value.hgt, suffix: '' }
        })
    }
    if (typeof zaraz !== 'undefined' && zaraz) {
        zaraz?.track('Add Cargo', {
            event_category: 'Calculator',
            event_action: 'Add Cargo',
            event_label: 'How many times new cargo was added for calculation.'
        })
    }
}
const onClickRemoveButton = (_i: number): void => {
    if (typeof zaraz !== 'undefined' && zaraz) {
        zaraz?.track('Remove Cargo', {
            event_category: 'Calculator',
            event_action: 'Remove Cargo',
            event_label: 'How many times cargo was removed from calculation.'
        })
    }

    data.splice(_i, 1)
}

// transition effects
const beforeEnter = (el: any) => {
    el.style.maxHeight = '0'
    el.style.opacity = '0'
    el.style.paddingTop = '0'
    el.style.paddingBottom = '0'
    el.style.marginTop = '4px'
}
const enter = (el: any) => {
    el.style.maxHeight = el.scrollHeight + 'px'
    el.style.opacity = '1'
    el.style.paddingTop = '0'
    el.style.paddingBottom = '0'
}
const afterEnter = (el: any) => {
    el.style.maxHeight = 'none'
    el.style.paddingTop = '0'
    el.style.paddingBottom = '0'
}
const beforeLeave = (el: any) => {
    el.style.maxHeight = el.scrollHeight + 'px'
    el.style.opacity = '1'
    el.style.paddingTop = '0.5rem'
    el.style.paddingBottom = '1rem'
}
const leave = (el: any) => {
    // Delay setting maxHeight to 0 to ensure the leave transition starts
    setTimeout(() => {
        el.style.maxHeight = '0'
        el.style.opacity = '0'
        el.style.paddingTop = '0'
        el.style.paddingBottom = '0'
        el.style.marginTop = '0px'
    }, 50)
}
const afterLeave = (el: any) => {
    el.style.maxHeight = 'none'
    el.style.paddingTop = '0'
    el.style.paddingBottom = '0'
}
const activeCompany = ref<string>('')
const activeCompanyId = ref<string>('')

// results
const summary = computed(() => {
    const formatNumbers = (value: number): string =>
        new Intl.NumberFormat('lv-LV', { useGrouping: true }).format(parseFloat(value.toFixed(2)))

    const result = data.reduce(
        (acc, item) => {
            acc.qty.value += item.qty.value || 0

            if (item.cbm && acc.cbm.value) {
                acc.cbm.value += (item.cbm.value || 0) * (item.qty.value || 0)
            }

            if (item.wgt && acc.wgt.value) {
                acc.wgt.value += (item.wgt.value || 0) * (item.qty.value || 0)
            }

            if (item.lth && acc.lth.value) {
                acc.lth.value += item.lth.value || 0
            }
            if (item.wth && acc.wth.value) {
                acc.wth.value += item.wth.value || 0
            }
            if (item.hgt && acc.hgt.value) {
                acc.hgt.value += item.hgt.value || 0
            }
            return acc
        },
        {
            cbm: { label: 'CBM in m³', value: 0, suffix: ' cbm' },
            qty: { label: 'Quantity', value: 0, suffix: ' items' },
            wgt: { label: 'Weight in kg', value: 0, suffix: ' kg' },
            lth: { label: 'Length in cm', value: 0, suffix: ' cm' },
            wth: { label: 'Width in cm', value: 0, suffix: ' cm' },
            hgt: { label: 'Height in cm', value: 0, suffix: ' cm' }
        }
    )

    const totalWeight = formatNumbers(result.wgt.value)

    // ldm based
    // ldm then match with kg's
    const ldm = computed(() =>
        parseFloat(
            data
                .reduce((total, item) => {
                    if (!item?.lth) {
                        return 0
                    }
                    const length = (item.lth.value || 0) / (units.value === 'cm' ? 100 : 1)
                    const width =
                        (item.wth && item.wth.value ? item.wth.value : 0 || 0) / (units.value === 'cm' ? 100 : 1)
                    const qty = item.qty.value || 0

                    return total + (length * width * qty) / 2.4
                }, 0)
                .toFixed(2)
        )
    )

    // cbm calculated from dimensions
    const cbm = computed(() =>
        parseFloat(
            data
                .reduce((total, item) => {
                    if (!item?.lth) {
                        if (item.cbm && item.cbm.value) {
                            const qty = item.qty.value || 0

                            return total + item.cbm.value * qty
                        }

                        return 0
                    }

                    const length = (item.lth.value || 0) / (units.value === 'cm' ? 100 : 1)
                    const width =
                        (item.wth && item.wth.value ? item.wth.value : 0 || 0) / (units.value === 'cm' ? 100 : 1)
                    const height =
                        (item.hgt && item.hgt.value ? item.hgt.value : 0 || 0) / (units.value === 'cm' ? 100 : 1)

                    const qty = item.qty.value || 0

                    return total + length * width * height * qty
                }, 0)
                .toFixed(2)
        )
    )

    // out ldm/cbm is rounded
    const roundedLdm = computed(() => Math.ceil(ldm.value * 5) / 5)
    //const roundedCbm = computed(() => Math.ceil(cbm.value * 2) / 2)

    // lowest delivery price
    // Calculate the lowest delivery price among all companies
    const lowestCompany = filteredCompanies.value.length
        ? filteredCompanies.value.reduce((prev: Company, curr: Company) => {
              const prevPrice = companyPrice(prev.companyId)
              const currPrice = companyPrice(curr.companyId)
              return currPrice < prevPrice ? curr : prev
          })
        : 0

    const lowestPrice = companyPrice(lowestCompany.companyId)

    return [
        {
            companyId: lowestCompany.companyId,
            companyName: lowestCompany.companyName,
            wgt: totalWeight + ' kg',
            ldm: roundedLdm.value + ' ldm',
            cbm: formatNumbers(cbm.value) + ' m³',
            pll: formatNumbers(Math.ceil(ldm.value / 0.4)) + ' pll',
            cost: (lowestPrice === Infinity ? 0 : lowestPrice) + ' €',
            qty: data.reduce((sum, item) => sum + (item.qty.value || 0), 0) + ' ' + t('details.items')
        }
    ]
})

const companyPrice = (companyId: string) => {
    if (!companyId) return 0

    let company = filteredCompanies.value.find((c: Company) => c.companyId === companyId)

    const convertKgTerminalityKg = (kg: number) => {
        if (kg < 1) return 0

        if (kg <= 1000) {
            const res = kg % 50 === 0 || kg <= 50 ? Math.round(kg / 50) * 50 : Math.ceil(kg / 50) * 50

            return res <= 0 ? 50 : res
        } else {
            return Math.ceil(kg / 100) * 100
        }
    }
    const convertElpMovingKg = (kg: number) => {
        if (kg < 1) return 0

        const res = kg % 150 === 0 || kg <= 150 ? Math.round(kg / 150) * 150 : Math.ceil(kg / 150) * 150

        return res <= 0 ? 150 : res
    }

    const result = data.reduce(
        (acc, item) => {
            acc.qty.value += item.qty.value || 0

            if (item.cbm && acc.cbm.value) {
                acc.cbm.value += (item.cbm.value || 0) * (item.qty.value || 0)
            }

            if (item.wgt && acc.wgt.value) {
                acc.wgt.value += (item.wgt.value || 0) * (item.qty.value || 0)
            }

            if (item.lth && acc.lth.value) {
                acc.lth.value += item.lth.value || 0
            }
            if (item.wth && acc.wth.value) {
                acc.wth.value += item.wth.value || 0
            }
            if (item.hgt && acc.hgt.value) {
                acc.hgt.value += item.hgt.value || 0
            }
            return acc
        },
        {
            cbm: { label: 'CBM in m³', value: 0, suffix: ' cbm' },
            qty: { label: 'Quantity', value: 0, suffix: ' items' },
            wgt: { label: 'Weight in kg', value: 0, suffix: ' kg' },
            lth: { label: 'Length in cm', value: 0, suffix: ' cm' },
            wth: { label: 'Width in cm', value: 0, suffix: ' cm' },
            hgt: { label: 'Height in cm', value: 0, suffix: ' cm' }
        }
    )

    const isTerminalityDeliveryPoint = companyId == '2'
    let realConvertedKg = isTerminalityDeliveryPoint
        ? convertKgTerminalityKg(result.wgt.value)
        : convertElpMovingKg(result.wgt.value)

    // ldm based
    // ldm then match with kg's
    const ldm = computed(() =>
        parseFloat(
            data
                .reduce((total, item) => {
                    if (!item?.lth) {
                        return 0
                    }
                    const length = (item.lth.value || 0) / (units.value === 'cm' ? 100 : 1)
                    const width =
                        (item.wth && item.wth.value ? item.wth.value : 0 || 0) / (units.value === 'cm' ? 100 : 1)
                    const qty = item.qty.value || 0
                    return total + (length * width * qty) / 2.4
                }, 0)
                .toFixed(2)
        )
    )

    // cbm calculated from dimensions
    const cbm = computed(() =>
        parseFloat(
            data
                .reduce((total, item) => {
                    if (!item?.lth) {
                        if (item.cbm && item.cbm.value) {
                            const qty = item.qty.value || 0

                            return total + item.cbm.value * qty
                        }

                        return 0
                    }

                    const length = (item.lth.value || 0) / (units.value === 'cm' ? 100 : 1)
                    const width =
                        (item.wth && item.wth.value ? item.wth.value : 0 || 0) / (units.value === 'cm' ? 100 : 1)
                    const height =
                        (item.hgt && item.hgt.value ? item.hgt.value : 0 || 0) / (units.value === 'cm' ? 100 : 1)

                    const qty = item.qty.value || 0

                    return total + length * width * height * qty
                }, 0)
                .toFixed(2)
        )
    )

    // out ldm/cbm is rounded
    const roundedLdm = computed(() => Math.ceil(ldm.value * 5) / 5)
    const roundedCbm = computed(() => Math.ceil(cbm.value * 2) / 2)

    const convertToKg = (object: any, value: number) => {
        const keys = Object.keys(object).sort((a: any, b: any) => a - b)

        const closest = keys.find((k: any) => k >= value) ?? keys[keys.length - 1]

        return Number(object[closest]) || 0
    }

    const convertToClosestKey = (object: Record<number, number>, value: number) => {
        const keys = Object.keys(object)
            .map(Number)
            .sort((a, b) => a - b)

        const closest = keys.find((k) => k >= value) ?? keys[keys.length - 1]

        return closest
    }

    // conversion
    const ldmToKg = convertToKg(company.ldmToKg, roundedLdm.value)
    const cbmToKg = convertToKg(company.cbmToKg, roundedCbm.value)

    const inputKg = realConvertedKg

    // find closest available kg
    const maximumKg = convertToClosestKey(company.kgToPrice, Math.max(ldmToKg, cbmToKg, inputKg))

    return company.kgToPrice[maximumKg] || 0
}

// address search from/to
// autocomplete google
const dfrom = ref('')
const dto = ref('')

const { suggestions: suggestionsFrom } = usePlacesAutocomplete(dfrom, {
    debounce: 250,
    minLengthAutocomplete: 2,
    apiKey: import.meta.env.VITE_MAPS_KEY,
    apiOptions: {
        language: 'en',
        region: 'US'
    }
})

const { suggestions: suggestionsTo } = usePlacesAutocomplete(dto, {
    debounce: 250,
    minLengthAutocomplete: 2,
    apiKey: import.meta.env.VITE_MAPS_KEY,
    apiOptions: {
        language: 'en',
        region: 'US'
    }
})

// is searching
const states = reactive({
    sfrom: false,
    sto: false
})

const defocusfrom = (value: boolean): void => {
    setTimeout(() => {
        states.sfrom = value
    }, 150)
}
const defocusto = (value: boolean): void => {
    setTimeout(() => {
        states.sto = value
    }, 200)
}

const renderCargoFields = ({
    item,
    index,
    id
}: {
    item: TInputField | string
    index: number
    id: string
}): VNode | undefined => {
    if (typeof item === 'string') return

    const handleValueUpdate = (value: number): void => {
        apps.isRessetable = false
        item.value = value
    }

    return h(
        IftaLabel,
        {
            class: [width.value < 1024 ? 'mb-2' : '']
        },
        {
            default: () => [
                h(
                    InputNumber,
                    {
                        ariaLabel:
                            item.suffix === ''
                                ? t('details.' + item.label, { units: units.value })
                                : t('details.' + item.label),

                        format: true,
                        highlightOnFocus: true,
                        modelValue: item.value,
                        'onUpdate:modelValue': handleValueUpdate,
                        inputId: id + '_' + index,
                        suffix:
                            item.suffix === ''
                                ? item.label === 'quantity'
                                    ? t('details.items')
                                    : ' ' + units.value
                                : item.suffix,
                        locale: 'lv-LV',
                        showButtons: !visibleOrder.value && !visibleAsk.value,
                        fluid: true,
                        maxFractionDigits: id !== 'qty' ? 2 : 0,
                        min: 0
                    },
                    {
                        incrementbuttonicon: () => h('span', { class: 'pi pi-plus' }),
                        decrementbuttonicon: () => h('span', { class: 'pi pi-minus' })
                    }
                ),
                h(
                    'label',
                    {
                        for: id + '_' + index
                    },
                    item.suffix === '' ? t('details.' + item.label, { units: units.value }) : t('details.' + item.label)
                )
            ]
        }
    )
}

const visibleAsk = ref<boolean>(false)
const visibleOrder = ref<boolean>(false)
const op = ref()
const op2 = ref()
const uid = ref<string>('')
const share = (event: Event | null) => {
    uid.value = uuid().split('-')[0]
    if (null !== event) op.value.toggle(event)
    // pass the value
    apps.onShare({
        activeCargo: apps.activeCargo,
        units: units.value,
        dfrom: dfrom.value,
        dto: dto.value,
        afrom: apps.afrom?.[0]?.formatted_address ?? apps.from?.description,
        ato: apps.ato?.[0]?.formatted_address ?? apps.to?.description,
        shared: uid.value,
        data
    })
    if (typeof zaraz !== 'undefined' && zaraz) {
        zaraz?.track('Share', {
            event_category: 'Results',
            event_action: 'Share',
            event_label: 'How many times the results were shared.'
        })
    }
}
const send = (event: Event) => {
    op2.value.toggle(event)
}

onBeforeMount(() => {
    init()

    const urlParams = new URLSearchParams(window.location.search)
    cargo.value = urlParams.get('cargo')

    if (apps.main.embed) {
        apps.from = {
            description: apps.main.from
        }
        apps.to = {
            description: apps.main.to
        }
        setTimeout(() => {
            apps.cargoPrice()
        }, 50)
    }
})

// autosearch
// used only in shared data automatic loading

// from
watch(
    () => suggestionsFrom.value,
    (item) => {
        if (!apps.main.autocomplete || !item.length) return
        setTimeout(() => {
            apps.setFrom(item[0])
        }, 100)
    }
)

// to
watch(
    () => suggestionsTo.value,
    (item) => {
        if (!apps.main.autocomplete || !item.length) return
        setTimeout(() => {
            apps.setTo(item[0])
        }, 200)
    }
)

// switcher between units
watch(
    () => [apps.activeCargo, units.value],
    () => {
        // cargo type change
        if (!apps.isRessetable) return
        init()
    }
)

// watch when cargo data is changed
watch(
    () => apps.isRessetable,
    (v1, v2) => {
        // cargo type change
        if (v1 === false && v2 === true) {
            if (typeof zaraz !== 'undefined' && zaraz) {
                zaraz?.track('Edit Cargo', {
                    event_category: 'Calculator',
                    event_action: 'Edit Cargo',
                    event_label: 'How many times the main cargo information was edited.'
                })
            }
        }
    }
)

// switcher between units
watch(
    () => [apps.activeCargo],
    () => {
        // cargo type change
        // switcher between cargo
        if (typeof zaraz !== 'undefined' && zaraz) {
            zaraz?.track('Select Cargo Type', {
                event_category: 'Calculator',
                event_action: 'Select Cargo Type',
                event_label:
                    'How many times the cargo type was selected (Letter, Docs, Box, Pallet, Van, Truck, Container).'
            })
        }
    }
)

// load shared cargo
watch(
    () => cargo.value,
    (sharedCargoKey) => {
        if (!sharedCargoKey) return
        setTimeout(() => {
            apps.loadSharedData(sharedCargoKey)

            toast.add({
                severity: 'info',
                summary: 'Cargo loaded!',
                group: 'auto',
                life: 3000
            })
        }, 50)
    }
)

// watch for data being loaded
watch(
    () => apps.main.initialized,
    (isInitialized) => {
        if (isInitialized === true) {
            // get cargo
            const sharedCargo = apps.main.shared?.data[0]

            if (!sharedCargo?.data?.length) return

            // load base data
            data.length = 0
            apps.isRessetable = false
            // loads items
            sharedCargo?.data?.forEach((item: TInput) => {
                data.push(item)
            })

            // loads default settings like units etc
            units.value = sharedCargo?.units
            apps.activeCargo = sharedCargo?.activeCargo

            // set to autocomplete
            dfrom.value = sharedCargo?.afrom
            dto.value = sharedCargo?.ato
        }
    }
)

// watch for changes being made with routes
watch(
    () => apps.from?.description,
    (rFrom: string) => {
        if (!apps.to?.description) return
        if (typeof zaraz !== 'undefined' && zaraz && rFrom && rFrom !== '') {
            zaraz?.track('Select Route', {
                event_category: 'Calculator',
                event_action: 'Select Route',
                event_label:
                    'From: ' +
                    rFrom +
                    ' To: ' +
                    (apps.ato?.[0]?.formatted_address ?? apps.to?.description) +
                    ' | Cargo: ' +
                    apps.activeCargo +
                    ' | Weight: ' +
                    summary.value[0].wgt +
                    'kg | CBM: ' +
                    summary.value[0].cbm +
                    'm³'
            })
        }
    }
)
watch(
    () => apps.to?.description,
    (rTo: string) => {
        if (!apps.from?.description) return
        if (zaraz && rTo && rTo !== '') {
            zaraz?.track('Select Route', {
                event_category: 'Calculator',
                event_action: 'Select Route',
                event_label:
                    'From: ' +
                    (apps.afrom?.[0]?.formatted_address ?? apps.from?.description) +
                    ' To: ' +
                    rTo +
                    ' | Cargo: ' +
                    apps.activeCargo +
                    ' | Weight: ' +
                    summary.value[0].wgt +
                    'kg | CBM: ' +
                    summary.value[0].cbm +
                    'm³'
            })
        }
    }
)

const order = (id: string, _company: string) => {
    visibleOrder.value = true
    activeCompany.value = _company
    activeCompanyId.value = id
    if (typeof zaraz !== 'undefined' && zaraz) {
        zaraz?.track('Order ' + _company, {
            event_category: 'Contact',
            event_action: 'Order ' + _company,
            event_label: 'How many times "ORDER" was clicked for a specific company.'
        })
    }
}

const ask = (id: string, _company: string) => {
    visibleAsk.value = true
    activeCompany.value = _company
    activeCompanyId.value = id
    if (typeof zaraz !== 'undefined' && zaraz) {
        zaraz?.track('Ask ' + _company, {
            event_category: 'Contact',
            event_action: 'Ask ' + _company,
            event_label: 'How many times "ASK" was clicked for a specific company.'
        })
    }
}

const print = () => {
    if (typeof zaraz !== 'undefined' && zaraz) {
        zaraz?.track('Print', {
            event_category: 'Results',
            event_action: 'Print',
            event_label: 'How many times the results were printed.'
        })
    }
    setTimeout(() => {
        window.print()
    }, 150)
}

const shareUrl = computed<string>((): string => {
    if (apps.main.embed) {
        return (
            'https://fromtocargo.com/costs/' +
            apps.main.from.toLowerCase() +
            '/' +
            apps.main.to.toLowerCase() +
            '/?cargo=' +
            uid.value
        )
    } else {
        return 'https://app.fromtocargo.com/?cargo=' + uid.value
    }
})

const copyText = (event: Event) => {
    op.value.toggle(event)
    navigator.clipboard.writeText(shareUrl.value)
}

const askForm = ref<AskForm>({
    name: '',
    country: '',
    company: '',
    email: '',
    phone: '',
    message: ''
})

const orderNowForm = ref<OrderNowForm>({
    name: '',
    country: '',
    company: '',
    companyCode: '',
    email: '',
    phone: '',
    date: new Date(),
    message: ''
})

const emailShareForm = ref<EmailShareForm>({
    email: ''
})

const askFormResolver = (e: FormResolverOptions) => {
    const errors: ValidationError = {}

    const trimmedValues: AskForm = Object.fromEntries(
        Object.entries(e.values as AskForm).map(([key, value]) => [key, value.trim()])
    ) as AskForm

    if (!trimmedValues.name) {
        errors.name = [{ message: 'Name is required.' }]
    } else if (trimmedValues.name.length > 255) {
        errors.name = [{ message: 'Name must be less than 255 characters.' }]
    }

    if (!trimmedValues.country) {
        errors.country = [{ message: 'Country is required.' }]
    } else if (trimmedValues.country.length > 255) {
        errors.country = [{ message: 'Country must be less than 255 characters.' }]
    }

    if (trimmedValues.company && trimmedValues.company.length > 255) {
        errors.company = [{ message: 'Company name must be less than 255 characters.' }]
    }

    if (!trimmedValues.email) {
        errors.email = [{ message: 'Email is required.' }]
    } else if (!/\S+@\S+\.\S+/.test(trimmedValues.email)) {
        errors.email = [{ message: 'Email is invalid.' }]
    } else if (trimmedValues.email.length > 255) {
        errors.email = [{ message: 'Email must be less than 255 characters.' }]
    }

    if (!trimmedValues.phone) {
        errors.phone = [{ message: 'Phone is required.' }]
    } else if (trimmedValues.phone.length > 255) {
        errors.phone = [{ message: 'Phone must be less than 255 characters.' }]
    }

    if (!trimmedValues.message) {
        errors.message = [{ message: 'Message is required.' }]
    } else if (trimmedValues.message.length > 2000) {
        errors.message = [{ message: 'Message must be less than 2000 characters.' }]
    }

    return {
        errors
    }
}
const orderNowFormResolver = (e: FormResolverOptions) => {
    const errors: ValidationError = {}

    const trimmedValues: OrderNowForm = Object.fromEntries(
        Object.entries(e.values as OrderNowForm).map(([key, value]) =>
            key === 'date' ? [key, value] : [key, (value as string).trim()]
        )
    ) as OrderNowForm

    if (trimmedValues.name == '') {
        errors.name = [{ message: 'Full Name is required.' }]
    } else if (trimmedValues.name.length > 255) {
        errors.name = [{ message: 'Full Name must be less than 255 characters.' }]
    }

    if (!trimmedValues.country) {
        errors.country = [{ message: 'Country is required.' }]
    } else if (trimmedValues.country.length > 255) {
        errors.country = [{ message: 'Country must be less than 255 characters.' }]
    }

    if (trimmedValues.company && trimmedValues.company.length > 255) {
        errors.company = [{ message: 'Company name must be less than 255 characters.' }]
    }

    if (trimmedValues.companyCode && trimmedValues.companyCode.length > 255) {
        errors.companyCode = [{ message: 'Company name must be less than 255 characters.' }]
    }

    if (trimmedValues.email && trimmedValues.email.length > 255) {
        errors.email = [{ message: 'Email must be less than 255 characters.' }]
    } else if (!/\S+@\S+\.\S+/.test(trimmedValues.email) && trimmedValues.email) {
        errors.email = [{ message: 'Email is invalid.' }]
    } else if (!trimmedValues.email) {
        errors.email = [{ message: 'Email is required.' }]
    }

    if (!trimmedValues.phone) {
        errors.phone = [{ message: 'Phone is required.' }]
    } else if (trimmedValues.phone.length > 255) {
        errors.phone = [{ message: 'Phone must be less than 255 characters.' }]
    }

    if (trimmedValues.message && trimmedValues.message.length > 2000) {
        errors.message = [{ message: 'Additional Information must be less than 2000 characters.' }]
    }

    // Date validation: required and not in the past

    const currentDate = new Date()

    if (!trimmedValues.date) {
        errors.date = [{ message: 'Preferred Delivery Date is required.' }]
    } else if (trimmedValues.date < currentDate) {
        errors.date = [{ message: 'Preferred Delivery Date cannot be today or in the past.' }]
    }

    return {
        errors
    }
}
const emailShareFormResolver = (e: FormResolverOptions) => {
    const errors: ValidationError = {}

    const trimmedValues: EmailShareForm = Object.fromEntries(
        Object.entries(e.values as EmailShareForm).map(([key, value]) => [key, value.trim()])
    ) as EmailShareForm

    if (trimmedValues.email && trimmedValues.email.length > 255) {
        errors.email = [{ message: 'Email must be less than 255 characters.' }]
    } else if (!/\S+@\S+\.\S+/.test(trimmedValues.email) && trimmedValues.email) {
        errors.email = [{ message: 'Email is invalid.' }]
    } else if (!trimmedValues.email) {
        errors.email = [{ message: 'Email is required.' }]
    }

    return {
        errors
    }
}

const visible = ref(false)
const onAskFormSubmit = (event: FormSubmitEvent) => {
    if (event.valid) {
        visibleAsk.value = false
        // @ts-ignore
        toast.add({
            severity: 'custom' as 'error' | 'warn' | 'success' | 'info' | 'secondary' | 'contrast',
            summary: 'Inquiry sent! The transport provider will get back to you soon.',
            group: 'headless',
            styleClass: 'backdrop-blur-lg rounded-2xl',
            life: 8000
        })

        // share
        share(null)

        setTimeout(() => {
            apps.askForm({
                name: event.states.name.value,
                country: event.states.country.value,
                company: event.states.company.value,
                email: event.states.email.value,
                phone: event.states.phone.value,
                message: event.states.message.value,

                // client data
                deliveryCompany: activeCompany.value,
                deliveryAddress: apps.ato?.[0]?.formatted_address ?? apps.to?.description,
                pickupAddress: apps.afrom?.[0]?.formatted_address ?? apps.from?.description,

                quantity: summary.value[0].qty,
                cbm: summary.value[0].cbm,
                weight: summary.value[0].wgt,
                pll: summary.value[0].pll,
                ldm: summary.value[0].ldm,
                cost: companyPrice(activeCompanyId.value),
                url: shareUrl.value
            })
        }, 1250)
    }
}

const onOrderNowFormSubmit = (event: FormSubmitEvent) => {
    if (event.valid) {
        visibleOrder.value = false
        // @ts-ignore
        toast.add({
            severity: 'custom' as 'error' | 'warn' | 'success' | 'info' | 'secondary' | 'contrast',
            summary: t('form.orderPlaced'),
            group: 'headless',
            styleClass: 'backdrop-blur-lg rounded-2xl',
            life: 8000
        })

        share(null)

        setTimeout(() => {
            apps.orderForm({
                name: event.states.name.value,
                country: event.states.country.value,
                company: event.states.company.value,
                companyCode: event.states.companyCode.value,
                email: event.states.email.value,
                phone: event.states.phone.value,
                date: event.states.date.value,
                message: event.states.message.value,
                // client data
                deliveryCompany: activeCompany.value,
                deliveryAddress: apps.ato?.[0]?.formatted_address ?? apps.to?.description,
                pickupAddress: apps.afrom?.[0]?.formatted_address ?? apps.from?.description,
                orderId: uid.value,

                quantity: summary.value[0].qty,
                cbm: summary.value[0].cbm,
                weight: summary.value[0].wgt,
                pll: summary.value[0].pll,
                ldm: summary.value[0].ldm,
                cost: companyPrice(activeCompanyId.value),
                url: shareUrl.value
            })

            if (typeof zaraz !== 'undefined' && zaraz) {
                // track event
                zaraz.ecommerce('Order Completed', {
                    checkout_id: uid.value,
                    order_id: uuid(),
                    affiliation: 'fromtocargo.com',
                    total: summary.value[0].cost,
                    revenue: summary.value[0].cost,
                    shipping: 0,
                    tax: 0,
                    discount: 0,
                    currency: 'EUR'
                })
            }
        }, 1250)
    }
}

const onSubmitEmailShare = (event: FormSubmitEvent) => {
    if (event.valid) {
        // share
        share(null)

        op2.value.toggle(event)

        // @ts-ignore
        toast.add({
            severity: 'custom' as 'error' | 'warn' | 'success' | 'info' | 'secondary' | 'contrast',
            summary: 'Thank you! Summary in to Your email has bent sent!',
            group: 'headless',
            styleClass: 'backdrop-blur-lg rounded-2xl',
            life: 3000
        })

        // send share email
        apps.shareForm({
            email: event.states.email.value,
            url: shareUrl.value
        })

        if (typeof zaraz !== 'undefined' && zaraz) {
            zaraz?.track('Send to Email', {
                event_category: 'Results',
                event_action: 'Send to Email',
                event_label: 'How many times the results were sent by email.'
            })
        }
    }
}

const changeUrl = (lang: string) => {
    locale.value = lang
    window.history.pushState(null, '', `/${lang}/`)
}

onMounted(() => {
    // rooms loader
    availableRooms.value = (rawRoomsData as { rooms: Room[] }).rooms
    const itemsList = (allItemsDataJson as { items: ItemDefinition[] }).items
    itemsList.forEach((item) => allItemsMap.value.set(item.id, item))

    // load rich snippet

    type AreaServedItem = {
        '@type': 'Continent' | 'Country'
        name: string
    }

    let names = [
        'Lietuva',
        'Latvija',
        'Estija',
        'Vokietija',
        'Austrija',
        'Slovakija',
        'Vengrija',
        'Čekija',
        'Helsinkis',
        'Prancūzija',
        'Ispanija',
        'Portugalija',
        'Londonas',
        'Danija',
        'Nyderlandai',
        'Belgija',
        'Stokholmas',
        'Italija',
        'Lenkija',
        'Graikija',
        'Bulgarija',
        'Šveicarija',
        'Serbija',
        'Kroatija',
        'Dublinas',
        'Liuksemburgas',
        'Rumunija',
        'Slovėnija'
    ]

    if (locale.value === 'en') {
        names = [
            'Lithuania',
            'Latvia',
            'Estonia',
            'Germany',
            'Austria',
            'Slovakia',
            'Hungary',
            'Czechia',
            'Helsinki',
            'France',
            'Spain',
            'Portugal',
            'London',
            'Denmark',
            'Netherlands',
            'Belgium',
            'Stockholm',
            'Italy',
            'Poland',
            'Greece',
            'Bulgaria',
            'Switzerland',
            'Serbia',
            'Croatia',
            'Dublin',
            'Luxembourg',
            'Romania',
            'Slovenia'
        ]
    }

    const areaServed = computed<AreaServedItem[]>(() => {
        const countryNames = names
        const continent: AreaServedItem = { '@type': 'Continent', name: 'Europe' }
        const countries: AreaServedItem[] = countryNames.map((name) => ({
            '@type': 'Country',
            name
        }))
        return [continent, ...countries]
    })

    let serviceType = ['Krovinių pervežimas', 'Siuntų pervežimas']
    if (locale.value === 'en') {
        serviceType = ['Freight Shipping', 'Parcel Delivery']
    }

    useHead({
        script: [
            // Rich Snippet / JSON-LD script
            {
                type: 'application/ld+json',
                // JSON.stringify converts your JSON object to a string
                innerHTML: JSON.stringify({
                    '@context': 'https://schema.org/',
                    '@type': 'Service',
                    name: t('snippets.name'),
                    description: t('snippets.description'),
                    provider: {
                        '@type': 'Organization',
                        name: 'FromToCargo',
                        url: 'https://app.fromtocargo.com',
                        logo: 'https://fromtocargo.com/images/logo.png',
                        contactPoint: {
                            '@type': 'ContactPoint',
                            telephone: '+44 2045 773957',
                            contactType: 'customer service',
                            email: 'hello@fromtocargo.com',
                            areaServed: [
                                'LT',
                                'LV',
                                'EE',
                                'DE',
                                'AT',
                                'SK',
                                'HU',
                                'CZ',
                                'FI',
                                'FR',
                                'ES',
                                'PT',
                                'GB',
                                'DK',
                                'NL',
                                'BE',
                                'SE',
                                'IT',
                                'PL',
                                'GR',
                                'BG',
                                'CH',
                                'RS',
                                'HR',
                                'IE',
                                'LU',
                                'RO',
                                'SI'
                            ]
                        }
                    },
                    areaServed: areaServed.value,
                    serviceType: serviceType,
                    offers: {
                        '@type': 'AggregateOffer',
                        priceCurrency: 'EUR',
                        lowPrice: '100',
                        highPrice: '2669',
                        offerCount: '1092',
                        url: 'https://app.fromtocargo.com/' + locale.value + '/',
                        validFrom: '2025-02-03'
                    },
                    sameAs: [
                        'https://www.facebook.com/fromtocargo/',
                        'https://www.linkedin.com/company/fromtocargo/',
                        'https://x.com/FromToCargo'
                    ]
                })
            }
        ]
    })

    // end
    if (apps.main.embed) return
    const currentLang = window.location.pathname.split('/')[1]
    if (!currentLang || !availableLocales.includes(currentLang)) {
        changeUrl(locale.value) // Set default lang if no valid lang is set
    }
})

const filteredCompanies = computed(() => {
    return (
        apps.prices.companies?.filter((company: Company) => {
            return company.kgToPrice && Object.keys(company.kgToPrice).length > 0
        }) || []
    ) // Return an empty array if companies is null or undefined
})

// rooms logic
const householdItemIdsInSelectedRooms = computed((): Set<string> => {
    const itemIds = new Set<string>()
    if (!selectionModeActive.value) return itemIds

    selectedRoomIds.value.forEach((roomId) => {
        const room = availableRooms.value.find((r) => r.id === roomId)
        room?.fields['Household Items']?.forEach((itemId) => itemIds.add(itemId))
    })
    return itemIds
})
const itemsForDisplayInScroller = computed((): ItemDefinition[] => {
    if (selectionStep.value !== 2) return []
    const ids = householdItemIdsInSelectedRooms.value
    let items = Array.from(ids)
        .map((id) => allItemsMap.value.get(id))
        .filter((item) => item !== undefined) as ItemDefinition[]

    if (searchQuery.value.trim() !== '') {
        const lowerSearchQuery = searchQuery.value.toLowerCase()
        items = items.filter((item) => item.fields['Item Name'].toLowerCase().includes(lowerSearchQuery))
    }
    return items
})

const totalSelectedRooms = computed((): number => selectedRoomIds.value.length)
const totalSelectedItemsWithQuantity = computed((): number => {
    return Object.values(itemQuantities).filter((qty) => qty > 0).length
})

const totalCalculatedCBM = computed((): number => {
    let currentTotalCbm = 0
    for (const itemId in itemQuantities) {
        const quantity = itemQuantities[itemId]
        if (quantity > 0) {
            const item = allItemsMap.value.get(itemId)
            if (item) {
                currentTotalCbm += item.fields['Volume (m3)'] * quantity
            }
        }
    }
    return parseFloat(currentTotalCbm.toFixed(3))
})

// methods for rooms
const toggleSelectionMode = (): void => {
    selectionModeActive.value = !selectionModeActive.value
    if (selectionModeActive.value) {
        selectedRoomIds.value = [] // Reset rooms when activating mode
        // No need to clear itemQuantities here, handled by watch on selectionModeActive
    } else {
        // init(); // Reset to manual CBM mode if needed
    }
}

const proceedToItemSelection = (): void => {
    if (selectedRoomIds.value.length > 0) {
        selectionStep.value = 2
        searchQuery.value = '' // Clear search when entering step 2
    }
}
const backToRoomSelection = (): void => {
    selectionStep.value = 1
    // Item quantities are preserved, user can adjust rooms and come back
}

//
watch(selectionModeActive, (isActive) => {
    if (!isActive) {
        selectionStep.value = 1 // Reset to room selection step
        Object.keys(itemQuantities).forEach((key) => delete itemQuantities[key]) // Clear all quantities
        searchQuery.value = ''
        selectedRoomIds.value = []
    }
})

const handleQuantityChange = (itemId: string, newQuantity: number | null): void => {
    if (newQuantity === null || newQuantity <= 0) {
        delete itemQuantities[itemId]
    } else {
        itemQuantities[itemId] = newQuantity
    }
}
</script>

<style>
@media (min-width: 1024px) {
    .item-address-block {
        position: relative;
    }
    .item-address-block-from {
        position: relative;
    }
}

@media (max-width: 1023px) {
    .item-address-block-fallback {
        position: relative;
    }
    .item-address-block-from {
        position: relative;
    }
}

.search-auto-complete-opts {
    position: absolute;
    top: 5rem;
    background: white;
    padding: 1rem;
    border: 1px solid gray;
    width: 100%;
    border-radius: 0.5rem;
    z-index: 9999;
    left: 0;
}

.search-auto-complete-opts-from {
    position: absolute;
    top: 5rem;
    background: white;
    padding: 1rem;
    border: 1px solid gray;
    width: 100%;
    border-radius: 0.5rem;
    z-index: 9999;
    left: 0;
}
</style>
<style>
.mobile-menu-wrapper {
    position: relative;
}
.nav-toggle {
    display: none;
}
.nav-content {
    display: none;
}
.nav-toggle:checked ~ header .nav-content {
    display: block;
}
.p-drawer-header {
    margin-top: 5rem;
}
</style>

<template>
    <div class="fixed top-0 left-0 right-0 z-50">
        <div class="mobile-menu-wrapper">
            <input type="checkbox" id="nav-toggle" class="nav-toggle" />
            <header class="fixed w-full top-0 z-50 bg-white/80 backdrop-blur-sm border-b border-gray-100">
                <div class="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
                    <nav class="flex items-center justify-between h-20">
                        <a href="https://fromtocargo.com/" class="flex items-center space-x-4">
                            <img
                                src="https://fromtocargo.com/images/logo.png"
                                alt="FromToCargo Logo"
                                class="h-12"
                                height="46px"
                                width="164px"
                            />
                        </a>
                        <div class="flex items-center space-x-2">
                            <img
                                v-for="lang in availableLocales"
                                @click="changeUrl(lang)"
                                :key="lang"
                                :src="`/${lang}.png`"
                                class="w-full h-full cursor-pointer"
                                style="height: 18px"
                                :alt="lang"
                            />
                        </div>
                        <div class="hidden md:flex items-center space-x-4">
                            <a
                                :href="'https://fromtocargo.com/' + locale + '/company'"
                                class="inline-flex items-center justify-center px-6 py-3 rounded-lg font-bold transition duration-200 ease-in-out border-2 border-gray-200 hover:border-primary-600 hover:text-primary-600"
                                >{{ t('nav.whatWeDo') }}</a
                            >
                            <a
                                :href="'https://fromtocargo.com/' + locale + '/contacts'"
                                class="inline-flex items-center justify-center px-6 py-3 rounded-lg font-bold transition duration-200 ease-in-out bg-primary-600 text-white hover:bg-primary-700 hover:scale-105"
                                >{{ t('nav.contactUs') }}</a
                            >
                        </div>
                        <div class="md:hidden">
                            <label
                                for="nav-toggle"
                                class="p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 transition-colors cursor-pointer"
                                ><svg class="w-6 h-6" fill="none" stroke="currentcolor" viewBox="0 0 24 24">
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M4 6h16M4 12h16M4 18h16"
                                    ></path></svg
                            ></label>
                        </div>
                    </nav>
                </div>
                <div
                    class="nav-content md:hidden w-full fixed left-0 right-0 top-20 bg-white border-t border-gray-100 shadow-lg"
                >
                    <div class="w-full px-6 py-4">
                        <div class="pt-4 space-y-4">
                            <a
                                :href="'https://fromtocargo.com/' + locale + '/company'"
                                class="block text-center px-6 py-3 rounded-lg font-bold transition duration-200 ease-in-out border-2 border-gray-200 hover:border-primary-600 hover:text-primary-600"
                                >{{ t('nav.whatWeDo') }}
                            </a>
                            <a
                                :href="'https://fromtocargo.com/' + locale + '/contacts'"
                                class="block text-center px-6 py-3 rounded-lg font-bold transition duration-200 ease-in-out bg-primary-600 text-white hover:bg-primary-700 hover:scale-105"
                                >{{ t('nav.contactUs') }}</a
                            >
                        </div>
                    </div>
                </div>
            </header>
        </div>
    </div>
    <main class="pt-20">
        <Panel>
            <template #header
                ><h1 class="text-4xl font-bold leading-tight mb-8">{{ t('topic.title') }} {{ cargo }}</h1></template
            >
            <p class="text-lg text-gray-600">{{ t('topic.description') }}</p>
        </Panel>

        <Panel class="mt-12">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-[2rem] item-address-block-fallback">
                <div class="item-address-block">
                    <label for="dto">{{ t('address.from') }}</label>
                    <IconField>
                        <InputText
                            size="large"
                            :placeholder="t('address.from')"
                            id="dfrom"
                            fluid
                            :value="states.sfrom ? dfrom : apps.afrom?.[0]?.formatted_address ?? apps.from?.description"
                            v-model="dfrom"
                            autocomplete="off"
                            @focus="defocusfrom(true)"
                            @blur="defocusfrom(false)"
                            :invalid="
                                apps.prices.status === 'error' &&
                                apps.from?.description !== undefined &&
                                apps.to?.description !== undefined
                            "
                        />
                        <InputIcon v-show="!visibleOrder && !visibleAsk" class="pi pi-search" />
                    </IconField>

                    <div v-if="states.sfrom && suggestionsFrom.length" class="search-auto-complete-opts">
                        <ScrollPanel
                            :style="{
                                width: '100%',
                                height: suggestionsFrom.length * 80 + 'px!important'
                            }"
                        >
                            <div
                                v-for="item in suggestionsFrom"
                                class="autocomplete-item"
                                style="min-height: 80px; border-radius: 1rem"
                                @mousedown="apps.setFrom(item)"
                            >
                                {{ item.description }}
                            </div>
                        </ScrollPanel>
                    </div>
                </div>

                <div class="item-address-block item-address-block-from">
                    <label for="dto">{{ t('address.to') }}</label>
                    <IconField>
                        <InputText
                            size="large"
                            :placeholder="t('address.to')"
                            id="dto"
                            fluid
                            :value="states.sto ? dto : apps.ato?.[0]?.formatted_address ?? apps.to?.description"
                            v-model="dto"
                            autocomplete="off"
                            @focus="defocusto(true)"
                            @blur="defocusto(false)"
                            :invalid="
                                apps.prices.status === 'error' &&
                                apps.from?.description !== undefined &&
                                apps.to?.description !== undefined
                            "
                        />
                        <InputIcon class="pi pi-search" v-show="!visibleOrder && !visibleAsk" />
                    </IconField>

                    <div v-if="states.sto && suggestionsTo.length" class="search-auto-complete-opts-from">
                        <ScrollPanel
                            :style="{
                                width: '100%',
                                height: suggestionsTo.length * 80 + 'px!important'
                            }"
                        >
                            <div
                                v-for="item in suggestionsTo"
                                class="autocomplete-item"
                                style="min-height: 80px; border-radius: 1rem"
                                @mousedown="apps.setTo(item)"
                            >
                                {{ item.description }}
                            </div>
                        </ScrollPanel>
                    </div>
                </div>
            </div>

            <Message
                severity="warn"
                variant="simple"
                icon="pi pi-times-circle"
                :life="8000"
                v-if="
                    apps.prices.status === 'error' &&
                    apps.from?.description !== undefined &&
                    apps.to?.description !== undefined &&
                    apps.hadHandShakeWithApi
                "
                >{{ apps.prices.message }}</Message
            >

            <div class="grid md:grid-cols-7 grid-cols-2 xl:gap-4 md:gap-2 gap-4 my-[3rem]">
                <Button
                    v-for="(item, i) in cargos"
                    :key="i"
                    :disabled="item.disabled"
                    :label="item.label"
                    :icon="item.icon"
                    rounded
                    :severity="i !== apps.activeCargo ? 'secondary' : 'contrast'"
                    @click="apps.activeCargo = i"
                />
            </div>

            <div class="mb-6 flex items-center pt-[2rem] pb-5">
                <h2 class="text-2xl font-bold">{{ t('details.addCargo') }}</h2>
                <Button
                    v-show="apps.activeCargo !== 'moving'"
                    class="ml-4 max-h-[42px] max-w-[42px]"
                    v-for="(unit, i) in ['cm', 'm']"
                    :key="i"
                    :label="unit"
                    rounded
                    severity="secondary"
                    :text="units !== unit"
                    @click="units = unit"
                />
            </div>

            <div class="my-6 flex justify-start" v-show="apps.activeCargo === 'moving'">
                <Button
                    :label="selectionModeActive ? t('details.enterCbmManually') : t('details.selectFromRooms')"
                    :icon="selectionModeActive ? 'pi pi-calculator' : 'pi pi-list-check'"
                    @click="toggleSelectionMode"
                    severity="secondary"
                    :outlined="!selectionModeActive"
                />
            </div>

            <div class="mb-6" v-show="apps.activeCargo === 'moving'">or</div>

            <TransitionGroup
                name="slide"
                @before-enter="beforeEnter"
                @enter="enter"
                @after-enter="afterEnter"
                @before-leave="beforeLeave"
                @leave="leave"
                @after-leave="afterLeave"
            >
                <div
                    v-for="(cargoItem, index) in data"
                    :key="cargoItem.uuid"
                    :class="[width < 1024 ? 'grid-cols-2' : 'grid-cols-5']"
                    class="grid gap-4 my-[2rem] mt-5"
                >
                    <renderCargoFields
                        v-for="(item, i) in cargoItem"
                        :item="item as TInputField"
                        :id="i"
                        :index="index"
                        :key="i"
                    />

                    <Button
                        :class="[width > 1024 || width < 768 ? 'col-spal-3' : 'col-span-2']"
                        class="max-h-[42px] mt-2"
                        severity="secondary"
                        rounded
                        @click="onClickAddMoreButton(index)"
                        :label="t('details.addMoreCargo')"
                    />

                    <Button
                        :class="[width > 1024 || width < 768 ? 'col-spal-3' : 'col-span-2']"
                        class="max-h-[42px] mt-2"
                        v-show="index > 0"
                        rounded
                        severity="contrast"
                        @click="onClickRemoveButton(index)"
                        :label="t('details.removeCargo')"
                    />
                </div>
            </TransitionGroup>

            <div v-if="selectionModeActive && apps.activeCargo === 'moving'">
                <div v-if="selectionStep === 1">
                    <h2 class="text-2xl font-bold pt-[2rem] pb-5">{{ t('details.selectRoomsTitle') }}</h2>
                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-3">
                        <div
                            v-for="room in availableRooms"
                            :key="room.id"
                            class="flex items-center p-2 border rounded-md hover:bg-surface-50 dark:hover:bg-surface-700 transition-colors duration-150"
                        >
                            <Checkbox
                                v-model="selectedRoomIds"
                                :inputId="'room-' + room.id"
                                :value="room.id"
                                class="mr-3"
                            />
                            <label :for="'room-' + room.id" class="cursor-pointer select-none">{{
                                room.fields['Room name']
                            }}</label>
                        </div>
                    </div>
                    <Message v-if="availableRooms.length === 0" severity="warn" :closable="false" class="mt-6">
                        {{ t('details.noRoomsAvailable') }}
                    </Message>
                    <Message v-else-if="selectedRoomIds.length === 0" severity="info" :closable="false" class="mt-6">
                        {{ t('details.pleaseSelectAtLeastOneRoom') }}
                    </Message>
                    <Message
                        v-if="selectedRoomIds.length > 0"
                        severity="success"
                        :closable="false"
                        class="mt-6 flex justify-between items-center p-4 rounded-lg"
                    >
                        <span class="text-base">
                            {{
                                t('details.selectedRoomsInfo') +
                                ' (' +
                                selectedRoomIds.length +
                                ' ' +
                                (selectedRoomIds.length === 1 ? t('details.room') : t('details.rooms')) +
                                ')'
                            }}
                        </span>
                        <Button
                            :label="t('details.nextStepItems')"
                            @click="proceedToItemSelection"
                            class="p-button-md"
                            icon="pi pi-arrow-right"
                            iconPos="right"
                        />
                    </Message>
                </div>

                <div v-else-if="selectionStep === 2" class="px-1 md:px-0">
                    <div
                        class="flex flex-col md:flex-row justify-between md:items-center pt-4 pb-5 mb-6 border-b dark:border-surface-700"
                    >
                        <Button
                            :label="t('details.backToRoomSelection')"
                            @click="backToRoomSelection"
                            icon="pi pi-arrow-left"
                            class="p-button-text p-button-secondary text-lg mb-3 md:mb-0"
                        />
                        <h2 class="text-3xl font-bold text-surface-800 dark:text-surface-100">
                            {{ t('details.selectItemsTitle') }}
                        </h2>
                    </div>

                    <div class="mb-8">
                        <InputText
                            v-model="searchQuery"
                            :placeholder="t('details.searchItemsPlaceholder')"
                            class="w-full p-inputtext-lg rounded-lg shadow-sm border-surface-300 dark:border-surface-600 focus:border-primary-500 dark:focus:border-primary-400"
                        />
                    </div>

                    <div class="grid grid-cols-1 lg:grid-cols-12 gap-x-6 gap-y-8">
                        <div class="lg:col-span-8">
                            <VirtualScroller
                                :items="itemsForDisplayInScroller"
                                :itemSize="100"
                                class="border dark:border-surface-700 rounded-lg shadow-sm h-[500px] lg:h-[600px]"
                                :delay="150"
                                :showLoader="true"
                                loaderIcon="pi-spinner pi-spin"
                            >
                                <template v-slot:item="{ item, options }">
                                    <div
                                        :class="[
                                            'flex items-center p-4 border-b dark:border-surface-600',
                                            options.odd
                                                ? 'bg-surface-0 dark:bg-surface-800'
                                                : 'bg-surface-50 dark:bg-surface-850'
                                        ]"
                                        style="height: 100px"
                                    >
                                        <Image
                                            :src="'/img/items/' + item.image"
                                            :alt="item.fields['Item Name']"
                                            width="64"
                                            height="64"
                                            class="mr-4 flex-shrink-0 rounded-lg shadow-md bg-surface-100 dark:bg-surface-700 p-1"
                                            imageClass="object-contain"
                                            preview
                                        />
                                        <div class="flex-grow overflow-hidden mr-3">
                                            <div
                                                class="font-semibold text-base md:text-lg truncate text-surface-800 dark:text-surface-100"
                                                :title="item.fields['Item Name']"
                                            >
                                                {{ item.fields['Item Name'] }}
                                            </div>
                                            <small class="text-surface-500 dark:text-surface-400"
                                                >{{ t('details.volume') }}: {{ item.fields['Volume (m3)'] }} m³</small
                                            >
                                        </div>
                                        <div class="w-[130px] flex-shrink-0">
                                            <InputNumber
                                                :modelValue="itemQuantities[item.id]"
                                                @update:modelValue="
                                                    (val: number | null) => handleQuantityChange(item.id, val)
                                                "
                                                :inputId="'qty-' + item.id"
                                                showButtons
                                                buttonLayout="horizontal"
                                                :min="0"
                                                placeholder="0"
                                                :step="1"
                                                incrementButtonIcon="pi pi-plus"
                                                decrementButtonIcon="pi pi-minus"
                                                inputClass="w-16 text-center text-base md:text-lg"
                                                class="p-inputnumber-md data-[pc-section=input]:py-2"
                                            />
                                        </div>
                                    </div>
                                </template>
                                <template v-slot:loader="{ options }">
                                    <div
                                        :class="[
                                            'flex items-center justify-center p-2 h-[100px]',
                                            options.odd
                                                ? 'bg-surface-0 dark:bg-surface-800'
                                                : 'bg-surface-50 dark:bg-surface-850'
                                        ]"
                                    >
                                        <i
                                            class="pi pi-spin pi-spinner text-3xl text-primary-500 dark:text-primary-400"
                                        ></i>
                                    </div>
                                </template>
                                <template v-if="itemsForDisplayInScroller.length === 0 && selectionStep === 2">
                                    <div class="p-10 text-center text-surface-500 dark:text-surface-400 text-lg">
                                        <i class="pi pi-inbox text-4xl mb-3"></i>
                                        <p v-if="householdItemIdsInSelectedRooms.size === 0">
                                            {{ t('details.noItemsForSelectedRooms') }}
                                        </p>
                                        <p v-else>{{ t('details.noItemsMatchSearch') }}</p>
                                    </div>
                                </template>
                            </VirtualScroller>
                        </div>
                        <div class="lg:col-span-4">
                            <Panel
                                :header="t('details.summaryStats')"
                                toggleable
                                :collapsed="false"
                                class="shadow-xl rounded-lg border dark:border-surface-700"
                            >
                                <ul class="list-none p-0 m-0 space-y-4 text-base">
                                    <li class="flex justify-between items-center pb-3 border-b dark:border-surface-600">
                                        <span class="text-surface-600 dark:text-surface-300"
                                            >{{ t('details.totalRoomsSelected') }}:</span
                                        >
                                        <strong class="text-surface-800 dark:text-surface-100 text-xl">{{
                                            totalSelectedRooms
                                        }}</strong>
                                    </li>
                                    <li class="flex justify-between items-center pb-3 border-b dark:border-surface-600">
                                        <span class="text-surface-600 dark:text-surface-300"
                                            >{{ t('details.totalItemsSelectedWithQty') }}:</span
                                        >
                                        <strong class="text-surface-800 dark:text-surface-100 text-xl">{{
                                            totalSelectedItemsWithQuantity
                                        }}</strong>
                                    </li>
                                    <li class="flex justify-between items-center pt-2">
                                        <span class="text-surface-700 dark:text-surface-200 font-semibold text-lg"
                                            >{{ t('details.totalCalculatedCBM') }}:</span
                                        >
                                        <strong class="text-2xl font-bold text-primary-600 dark:text-primary-400"
                                            >{{ totalCalculatedCBM }} m³</strong
                                        >
                                    </li>
                                </ul>
                            </Panel>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else-if="apps.activeCargo === 'moving'">
                <p class="py-4">{{ t('details.manualCbmInputActive') }}</p>
            </div>

            <template #footer>
                <div class="flex gap-2 items-center pb-5">
                    <span class="text-xl">{{ t('details.cost').split(',')[0] }}: {{ summary[0].cost }}</span>
                    <Button
                        :disabled="parseFloat(summary[0].cost.split(' ')[0]) <= 0"
                        :label="t('info.order')"
                        @click="order(summary[0].companyId, summary[0].companyName)"
                        severity="contrast"
                        raised
                        variant="outlined"
                    />
                </div>
            </template>
        </Panel>
    </main>
    <footer>
        <Panel>
            <template #header>
                <h2 class="text-2xl font-bold mt-8 mb-4">{{ t('details.summary') }}</h2>
            </template>

            <div class="flex items-center gap-2 px-7 mb-2">
                <div>{{ t('details.origin') }}</div>
                <Tag
                    style="
                        border: 2px solid #e2e8f0;
                        background: transparent;
                        color: rgb(55, 65, 81);
                        border-radius: 2rem;
                    "
                >
                    <div class="flex items-center gap-2 px-2 py-1">
                        <img
                            v-if="apps.flag.from"
                            src="./flag_placeholder.png"
                            alt="flag"
                            class="flag"
                            :class="['flag-' + apps.flag.from]"
                            style="width: 18px"
                        />
                        <span class="text-base">{{
                            apps.countryFrom === 'is not selected' ? t('details.notSelected') : apps.countryFrom
                        }}</span>
                    </div>
                </Tag>
            </div>

            <div class="flex items-center gap-2 px-7 mb-2">
                <div>{{ t('details.destination') }}</div>

                <Tag
                    style="
                        border: 2px solid #e2e8f0;
                        background: transparent;
                        color: rgb(55, 65, 81);
                        border-radius: 2rem;
                    "
                >
                    <div class="flex items-center gap-2 px-2 py-1">
                        <img
                            v-if="apps.flag.to"
                            src="./flag_placeholder.png"
                            alt="flag"
                            class="flag"
                            :class="['flag-' + apps.flag.to]"
                            style="width: 18px"
                        />
                        <span class="text-base">{{
                            apps.countryTo === 'is not selected' ? t('details.notSelected') : apps.countryTo
                        }}</span>
                    </div>
                </Tag>
            </div>

            <div class="mt-8 mb-4">
                <Divider />
            </div>

            <p v-show="apps.prices.message !== '' && apps.prices.status === 'ok'" class="text-lg text-gray-600 mb-6">
                {{ apps.prices.message }}
            </p>

            <table
                class="min-w-full border-collapse border border-gray-300"
                style="width: 100% !important; max-width: 1280px !important"
                v-if="width >= 910"
            >
                <thead>
                    <tr class="bg-gray-100">
                        <th class="px-4 py-3 text-left border border-gray-300 th-color-and-weight">
                            {{ t('details.quantity') }}
                        </th>
                        <th class="px-4 py-3 text-left border border-gray-300 th-color-and-weight">
                            {{ t('details.cbm') }}
                        </th>
                        <th class="px-4 py-3 text-left border border-gray-300 th-color-and-weight">
                            {{ t('details.weight') }}
                        </th>
                        <th class="px-4 py-3 text-left border border-gray-300 th-color-and-weight">
                            {{ t('details.pll') }}
                        </th>
                        <th class="px-4 py-3 text-left border border-gray-300 th-color-and-weight">
                            {{ t('details.ldm') }}
                        </th>
                        <th class="px-4 py-3 text-left border border-gray-300 th-color-and-weight">
                            {{ t('details.cost') }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(row, index) in summary" :key="index" class="border-t border-gray-300">
                        <td class="px-4 py-3 border border-gray-300">{{ row.qty }}</td>
                        <td class="px-4 py-3 border border-gray-300">{{ row.cbm }}</td>
                        <td class="px-4 py-3 border border-gray-300">{{ row.wgt }}</td>
                        <td class="px-4 py-3 border border-gray-300">{{ row.pll }}</td>
                        <td class="px-4 py-3 border border-gray-300">{{ row.ldm }}</td>
                        <td class="px-4 py-3 border border-gray-300">{{ row.cost }}</td>
                    </tr>
                </tbody>
            </table>
            <div v-else class="xl:w-96 py-5 flex flex-col justify-between">
                <div class="flex flex-col gap-6 px-7" v-for="sum in summary">
                    <div class="flex items-center gap-2">
                        <div class="w-2 h-2 rounded-full" style="background-color: rgb(113, 113, 121)"></div>
                        <div class="text-color uppercase font-medium leading-6 flex-1">{{ t('details.quantity') }}</div>
                        <div class="leading-6 font-medium text-color">
                            {{ sum.qty }}
                        </div>
                    </div>
                    <div class="flex items-center gap-2">
                        <div class="w-2 h-2 rounded-full" style="background-color: rgb(113, 113, 121)"></div>
                        <div class="text-color uppercase font-medium leading-6 flex-1">{{ t('details.cbm') }}</div>
                        <div class="leading-6 font-medium text-color">{{ sum.cbm }}</div>
                    </div>
                    <div class="flex items-center gap-2">
                        <div class="w-2 h-2 rounded-full" style="background-color: rgb(113, 113, 121)"></div>
                        <div class="text-color uppercase font-medium leading-6 flex-1">{{ t('details.weight') }}</div>
                        <div class="leading-6 font-medium text-color">{{ sum.wgt }}</div>
                    </div>
                    <div class="flex items-center gap-2">
                        <div class="w-2 h-2 rounded-full" style="background-color: rgb(113, 113, 121)"></div>
                        <div class="text-color uppercase font-medium leading-6 flex-1">{{ t('details.pll') }}</div>
                        <div class="leading-6 font-medium text-color">{{ sum.pll }}</div>
                    </div>

                    <div class="flex items-center gap-2">
                        <div class="w-2 h-2 rounded-full" style="background-color: rgb(113, 113, 121)"></div>
                        <div class="text-color uppercase font-medium leading-6 flex-1">{{ t('details.ldm') }}</div>
                        <div class="leading-6 font-medium text-color">{{ sum.ldm }}</div>
                    </div>

                    <Divider class="!m-0" />

                    <div class="flex items-center gap-2 text-base">
                        <div class="w-2 h-2 rounded-full" style="background-color: rgb(113, 113, 121)"></div>
                        <div class="text-color uppercase font-medium leading-6 flex-1">{{ t('details.cost') }}</div>
                        <div class="leading-6 text-color font-bold">{{ sum.cost }}</div>
                    </div>
                </div>
            </div>

            <template #footer>
                <div class="flex items-center gap-4 flex-wrap md:justify-start my-4 justify-evenly">
                    <Button
                        icon="pi pi-print"
                        severity="secondary"
                        rounded
                        text
                        :label="t('buttons.print')"
                        @click="print"
                    />
                    <Button
                        icon="pi pi-share-alt"
                        severity="secondary"
                        rounded
                        text
                        :label="t('buttons.share')"
                        @click="share"
                    />
                    <Popover ref="op">
                        <div class="flex flex-col gap-4 md:w-[25rem] w-full">
                            <div>
                                <span class="font-medium block mb-2">{{ t('buttons.shareThis') }}</span>
                                <InputGroup>
                                    <InputText :value="shareUrl" readonly class="w-[25rem]"></InputText>
                                    <InputGroupAddon @click="copyText">
                                        <i class="pi pi-copy cursor-pointer"></i>
                                    </InputGroupAddon>
                                </InputGroup>
                            </div>
                        </div>
                    </Popover>
                    <Button
                        icon="pi pi-send"
                        severity="secondary"
                        rounded
                        text
                        :label="t('buttons.sendToEmail')"
                        @click="send"
                    />
                    <Popover ref="op2">
                        <div class="flex flex-col gap-4 md:w-[25rem] w-full">
                            <Form
                                v-slot="$form"
                                :initialValues="emailShareForm"
                                :resolver="emailShareFormResolver"
                                :validateOnValueUpdate="false"
                                :validateOnBlur="false"
                                @submit="onSubmitEmailShare"
                                class=""
                            >
                                <span class="font-medium block mb-2">{{ t('buttons.enterEmail') }}</span>
                                <InputGroup>
                                    <InputText name="email" autocomplete="off" type="text" autofocus />
                                    <Button
                                        :label="t('buttons.send')"
                                        type="submit"
                                        icon="pi pi-send"
                                        severity="contrast"
                                    />
                                </InputGroup>
                                <Message v-if="$form.email?.invalid" severity="error" size="small" variant="simple">{{
                                    $form.email.error.message
                                }}</Message>
                                <Message severity="secondary" variant="outlined" icon="pi pi-info" class="mt-3">{{
                                    t('buttons.policy')
                                }}</Message>
                            </Form>
                        </div>
                    </Popover>
                </div>
            </template>
        </Panel>

        <Panel class="mt-12" v-show="apps.prices.message !== '' && apps.prices.status === 'ok'" v-if="width >= 910">
            <template #header>
                <h2 class="text-2xl font-bold mt-8 mb-4">{{ t('info.companiesInRoute') }}</h2>
            </template>

            <table
                class="min-w-full border-collapse border border-gray-300 mb-5"
                style="width: 100% !important; max-width: 1280px !important"
            >
                <thead>
                    <tr>
                        <th class="px-4 py-3 text-left border border-gray-300 th-color-and-weight">
                            {{ t('info.company') }}
                        </th>
                        <th class="px-4 py-3 text-left border border-gray-300 th-color-and-weight">
                            {{ t('info.name') }}
                        </th>
                        <th class="px-4 py-3 text-left border border-gray-300 th-color-and-weight">
                            {{ t('info.cost') }}
                        </th>
                        <th class="px-4 py-3 text-left border border-gray-300 th-color-and-weight">
                            {{ t('info.rating') }}
                        </th>
                        <th class="px-4 py-3 text-left border border-gray-300 th-color-and-weight">
                            {{ t('info.action') }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(cRow, index) in filteredCompanies" :key="index" class="border-t border-gray-300">
                        <td class="px-4 py-3 border border-gray-300">
                            <img :src="cRow.companyLogo" :alt="cRow.companyName" width="160px" height="46px" />
                        </td>
                        <td class="px-4 py-3 border border-gray-300">{{ cRow.companyName }}</td>
                        <td class="px-4 py-3 border border-gray-300">{{ companyPrice(cRow.companyId) }} €</td>
                        <td class="px-4 py-3 border border-gray-300">
                            <div class="flex gap-1 items-center">
                                <svg
                                    v-for="_r in parseInt(cRow.companyRating)"
                                    :key="_r"
                                    width="14"
                                    height="14"
                                    viewBox="0 0 14 14"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    class="p-icon p-rating-on-icon"
                                    style="color: black"
                                    aria-hidden="true"
                                    data-pc-section="onicon"
                                >
                                    <path
                                        d="M13.9718 5.36453C13.9398 5.26298 13.8798 5.17252 13.7986 5.10356C13.7175 5.0346 13.6186 4.98994 13.5132 4.97472L9.37043 4.37088L7.51307 0.617955C7.46021 0.529271 7.38522 0.455834 7.29545 0.404836C7.20568 0.353838 7.1042 0.327026 7.00096 0.327026C6.89771 0.327026 6.79624 0.353838 6.70647 0.404836C6.6167 0.455834 6.54171 0.529271 6.48885 0.617955L4.63149 4.37088L0.488746 4.97472C0.383363 4.98994 0.284416 5.0346 0.203286 5.10356C0.122157 5.17252 0.0621407 5.26298 0.03014 5.36453C-0.00402286 5.46571 -0.00924428 5.57442 0.0150645 5.67841C0.0393733 5.7824 0.0922457 5.87753 0.167722 5.95308L3.17924 8.87287L2.4684 13.0003C2.45038 13.1066 2.46229 13.2158 2.50278 13.3157C2.54328 13.4156 2.61077 13.5022 2.6977 13.5659C2.78477 13.628 2.88746 13.6644 2.99416 13.6712C3.10087 13.678 3.20733 13.6547 3.30153 13.6042L7.00096 11.6551L10.708 13.6042C10.79 13.6491 10.882 13.6728 10.9755 13.673C11.0958 13.6716 11.2129 13.6343 11.3119 13.5659C11.3988 13.5022 11.4663 13.4156 11.5068 13.3157C11.5473 13.2158 11.5592 13.1066 11.5412 13.0003L10.8227 8.87287L13.8266 5.95308C13.9033 5.87835 13.9577 5.7836 13.9833 5.67957C14.009 5.57554 14.005 5.4664 13.9718 5.36453Z"
                                        fill="currentColor"
                                    ></path>
                                </svg>
                            </div>
                        </td>
                        <td class="px-4 py-3 border border-gray-300">
                            <div class="flex gap-3 py-4">
                                <a
                                    href="javascript:"
                                    class="text-center px-6 py-3 rounded-lg !text-base font-bold transition duration-200 ease-in-out border-2 border-gray-200 hover:border-primary-600 hover:text-primary-600"
                                    @click="order(cRow.companyId, cRow.companyName)"
                                    >{{ t('info.order') }}
                                </a>
                                <a
                                    href="javascript:"
                                    class="text-center px-6 py-3 rounded-lg transition duration-200 ease-in-out border-2 border-gray-200 hover:border-primary-600 hover:text-primary-600"
                                    @click="ask(cRow.companyId, cRow.companyName)"
                                    >{{ t('info.ask') }}
                                </a>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </Panel>
        <div
            v-show="apps.prices.message !== '' && apps.prices.status === 'ok'"
            v-else
            class="xl:w-96 border border-surface rounded-2xl py-5 px-7 flex flex-col justify-between mt-12"
        >
            <h2 class="text-2xl font-bold my-8">{{ t('info.companiesInRoute') }}</h2>
            <template v-for="(company, index) in filteredCompanies" :key="index">
                <div class="flex flex-col gap-6 my-4 px-7">
                    <div class="flex items-center gap-2">
                        <div class="w-2 h-2 rounded-full" style="background-color: rgb(245, 158, 11)"></div>
                        <div class="text-color uppercase font-medium leading-6 flex-1">{{ t('info.company') }}</div>
                        <div class="leading-6 font-medium text-color">
                            <img :src="company.companyLogo" :alt="company.companyName" width="160px" height="46px" />
                        </div>
                    </div>
                    <div class="flex items-center gap-2">
                        <div class="w-2 h-2 rounded-full" style="background-color: rgb(245, 158, 11)"></div>
                        <div class="text-color uppercase font-medium leading-6 flex-1">{{ t('info.name') }}</div>
                        <div class="leading-6 font-medium text-color">{{ company.companyName }}</div>
                    </div>

                    <div class="flex items-center gap-2 mt-2">
                        <div class="w-2 h-2 rounded-full" style="background-color: rgb(245, 158, 11)"></div>
                        <div class="text-color uppercase font-medium leading-6 flex-1">
                            {{ t('info.cost') }}
                        </div>
                        <div class="leading-6 font-medium text-color">{{ companyPrice(company.companyId) }} €</div>
                    </div>
                    <div class="flex items-center gap-2 mt-2">
                        <div class="w-2 h-2 rounded-full" style="background-color: rgb(245, 158, 11)"></div>
                        <div class="text-color uppercase font-medium leading-6 flex-1">
                            {{ t('info.rating') }} <span class="text-muted-color">(5/5)</span>
                        </div>
                        <div class="leading-6 font-medium text-color">
                            <div class="flex gap-1 items-center">
                                <svg
                                    v-for="_r in parseInt(company.companyRating)"
                                    :key="_r"
                                    width="14"
                                    height="14"
                                    viewBox="0 0 14 14"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    class="p-icon p-rating-on-icon"
                                    style="color: black"
                                    aria-hidden="true"
                                    data-pc-section="onicon"
                                >
                                    <path
                                        d="M13.9718 5.36453C13.9398 5.26298 13.8798 5.17252 13.7986 5.10356C13.7175 5.0346 13.6186 4.98994 13.5132 4.97472L9.37043 4.37088L7.51307 0.617955C7.46021 0.529271 7.38522 0.455834 7.29545 0.404836C7.20568 0.353838 7.1042 0.327026 7.00096 0.327026C6.89771 0.327026 6.79624 0.353838 6.70647 0.404836C6.6167 0.455834 6.54171 0.529271 6.48885 0.617955L4.63149 4.37088L0.488746 4.97472C0.383363 4.98994 0.284416 5.0346 0.203286 5.10356C0.122157 5.17252 0.0621407 5.26298 0.03014 5.36453C-0.00402286 5.46571 -0.00924428 5.57442 0.0150645 5.67841C0.0393733 5.7824 0.0922457 5.87753 0.167722 5.95308L3.17924 8.87287L2.4684 13.0003C2.45038 13.1066 2.46229 13.2158 2.50278 13.3157C2.54328 13.4156 2.61077 13.5022 2.6977 13.5659C2.78477 13.628 2.88746 13.6644 2.99416 13.6712C3.10087 13.678 3.20733 13.6547 3.30153 13.6042L7.00096 11.6551L10.708 13.6042C10.79 13.6491 10.882 13.6728 10.9755 13.673C11.0958 13.6716 11.2129 13.6343 11.3119 13.5659C11.3988 13.5022 11.4663 13.4156 11.5068 13.3157C11.5473 13.2158 11.5592 13.1066 11.5412 13.0003L10.8227 8.87287L13.8266 5.95308C13.9033 5.87835 13.9577 5.7836 13.9833 5.67957C14.009 5.57554 14.005 5.4664 13.9718 5.36453Z"
                                        fill="currentColor"
                                    ></path>
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="w-full py-4">
                    <div class="pt-4 space-y-4">
                        <a
                            href="javascript:"
                            class="block text-center px-6 py-3 rounded-lg !text-base font-bold transition duration-200 ease-in-out border-2 border-gray-200 hover:border-primary-600 hover:text-primary-600"
                            @click="order(company.companyId, company.companyName)"
                            >{{ t('info.order') }}
                        </a>
                        <a
                            href="javascript:"
                            class="block text-center px-6 py-3 rounded-lg transition duration-200 ease-in-out border-2 border-gray-200 hover:border-primary-600 hover:text-primary-600"
                            @click="ask(company.companyId, company.companyName)"
                            >{{ t('info.ask') }}
                        </a>
                    </div>
                </div>
            </template>
        </div>
    </footer>

    <Drawer
        v-model:visible="visibleAsk"
        :header="t('form.askForm')"
        position="full"
        class="max-w-7xl"
        :baseZIndex="99999999999999"
        :autoZIndex="false"
        :blockScroll="true"
        style="z-index: 9999999999 !important"
    >
        <div class="flex justify-center flex-col gap-4">
            <Form
                v-slot="$form"
                :initialValues="askForm"
                :resolver="askFormResolver"
                :validateOnValueUpdate="false"
                :validateOnBlur="true"
                @submit="onAskFormSubmit"
                class="flex justify-center flex-col gap-4"
            >
                <div class="flex flex-col gap-1">
                    <FloatLabel variant="in">
                        <InputText id="aName" fluid name="name" autocomplete="off" autofocus type="text" />
                        <label for="aName">{{ t('form.name') }}</label>
                    </FloatLabel>
                    <Message v-if="$form.name?.invalid" severity="error" autofocus size="small" variant="simple">{{
                        $form.name.error.message
                    }}</Message>
                </div>
                <div class="flex flex-col gap-1">
                    <FloatLabel variant="in">
                        <InputText id="aCountry" fluid name="country" autocomplete="off" type="text" />
                        <label for="aCountry">{{ t('form.country') }}</label>
                    </FloatLabel>
                    <Message v-if="$form.country?.invalid" severity="error" size="small" variant="simple">{{
                        $form.country.error.message
                    }}</Message>
                </div>
                <div class="flex flex-col gap-1">
                    <FloatLabel variant="in">
                        <InputText id="aCompany" fluid name="company" autocomplete="off" type="text" />
                        <label for="aCompany">{{ t('form.company') }}</label>
                    </FloatLabel>
                    <Message v-if="$form.company?.invalid" severity="error" size="small" variant="simple">{{
                        $form.company.error.message
                    }}</Message>
                </div>
                <div class="flex flex-col gap-1">
                    <FloatLabel variant="in">
                        <InputText id="aEmail" fluid name="email" autocomplete="off" type="email" />
                        <label for="aEmail">{{ t('form.email') }}</label>
                    </FloatLabel>
                    <Message v-if="$form.email?.invalid" severity="error" size="small" variant="simple">{{
                        $form.email.error.message
                    }}</Message>
                </div>
                <div class="flex flex-col gap-1">
                    <FloatLabel variant="in">
                        <InputText id="aPhone" fluid name="phone" autocomplete="off" type="text" />
                        <label for="aPhone">{{ t('form.phone') }}</label>
                    </FloatLabel>
                    <Message v-if="$form.phone?.invalid" severity="error" size="small" variant="simple">{{
                        $form.phone.error.message
                    }}</Message>
                </div>

                <div class="flex flex-col gap-1">
                    <IftaLabel>
                        <Textarea
                            id="aMessage"
                            :placeholder="t('form.enterYourQuestion')"
                            fluid
                            name="message"
                            rows="3"
                            autoResize
                            style="resize: none"
                        />
                        <label for="aMessage">{{ t('form.yourQuestion') }}</label>
                    </IftaLabel>
                    <Message v-if="$form.message?.invalid" severity="error" size="small" variant="simple">{{
                        $form.message.error.message
                    }}</Message>
                </div>

                <Message severity="secondary" variant="outlined" icon="pi pi-info">{{ t('buttons.policy') }}</Message>

                <Button type="submit" severity="secondary" :label="t('buttons.send')" />
            </Form>
        </div>
    </Drawer>
    <Drawer
        v-model:visible="visibleOrder"
        :header="t('form.orderForm')"
        position="full"
        class="max-w-7xl"
        :baseZIndex="99999999999999"
        :autoZIndex="false"
        :blockScroll="true"
        style="z-index: 9999999999 !important"
    >
        <div class="flex justify-center flex-col gap-4">
            <Form
                v-slot="$form"
                :initialValues="orderNowForm"
                :resolver="orderNowFormResolver"
                :validateOnValueUpdate="false"
                :validateOnBlur="true"
                @submit="onOrderNowFormSubmit"
                class="flex justify-center flex-col gap-4"
            >
                <div class="flex flex-col gap-1">
                    <FloatLabel variant="in">
                        <InputText id="aName" fluid name="name" autofocus autocomplete="off" type="text" />
                        <label for="aName">{{ t('form.fullName') }}</label>
                    </FloatLabel>
                    <Message v-if="$form.name?.invalid" severity="error" size="small" variant="simple">{{
                        $form.name.error.message
                    }}</Message>
                </div>

                <div class="flex flex-col gap-1">
                    <FloatLabel variant="in">
                        <InputText id="aCountry" fluid name="country" autocomplete="off" type="text" />
                        <label for="aCountry">{{ t('form.country') }}</label>
                    </FloatLabel>
                    <Message v-if="$form.country?.invalid" severity="error" size="small" variant="simple">{{
                        $form.country.error.message
                    }}</Message>
                </div>
                <div class="flex flex-col gap-1">
                    <FloatLabel variant="in">
                        <InputText id="aCompany" fluid name="company" autocomplete="off" type="text" />
                        <label for="aCompany">{{ t('form.company') }}</label>
                    </FloatLabel>
                    <Message v-if="$form.company?.invalid" severity="error" size="small" variant="simple">{{
                        $form.company.error.message
                    }}</Message>
                </div>
                <div class="flex flex-col gap-1">
                    <FloatLabel variant="in">
                        <InputText id="aCode" fluid name="companyCode" autocomplete="off" type="text" />
                        <label for="aCode">{{ t('form.companyCode') }}</label>
                    </FloatLabel>
                    <Message v-if="$form.company?.invalid" severity="error" size="small" variant="simple">{{
                        $form.companyCode.error.message
                    }}</Message>
                </div>
                <div class="flex flex-col gap-1">
                    <FloatLabel variant="in">
                        <InputText id="aEmail" fluid name="email" autocomplete="off" type="email" />
                        <label for="aEmail">{{ t('form.email') }}</label>
                    </FloatLabel>
                    <Message v-if="$form.email?.invalid" severity="error" size="small" variant="simple">{{
                        $form.email.error.message
                    }}</Message>
                </div>
                <div class="flex flex-col gap-1">
                    <FloatLabel variant="in">
                        <InputText id="aPhone" fluid name="phone" autocomplete="off" type="text" />
                        <label for="aPhone">{{ t('form.phone') }}</label>
                    </FloatLabel>
                    <Message v-if="$form.phone?.invalid" severity="error" size="small" variant="simple">{{
                        $form.phone.error.message
                    }}</Message>
                </div>

                <div class="flex flex-col gap-1">
                    <FloatLabel variant="in">
                        <DatePicker
                            name="date"
                            fluid
                            inputId="aDate"
                            dateFormat="yy-mm-dd"
                            showIcon
                            iconDisplay="input"
                            showButtonBar
                        />
                        <label for="aDate">{{ t('form.date') }}</label>
                    </FloatLabel>
                    <Message v-if="$form.date?.invalid" severity="error" size="small" variant="simple">{{
                        $form.date.error.message
                    }}</Message>
                </div>

                <div class="flex flex-col gap-1">
                    <IftaLabel>
                        <Textarea
                            id="aMessage"
                            :placeholder="t('form.notes')"
                            fluid
                            name="message"
                            rows="3"
                            autoResize
                            style="resize: none"
                        />
                        <label for="aMessage">{{ t('form.notes') }}</label>
                    </IftaLabel>
                    <Message v-if="$form.message?.invalid" severity="error" size="small" variant="simple">{{
                        $form.message.error.message
                    }}</Message>
                </div>

                <Message severity="secondary" variant="outlined" icon="pi pi-info">{{ t('buttons.policy') }}</Message>

                <Button type="submit" severity="secondary" :label="t('buttons.send')" />
            </Form>
        </div>
    </Drawer>
    <Toast position="top-center" group="headless" @close="visible = false">
        <template #container="{ message, closeCallback }">
            <section class="flex flex-col p-4 gap-4 w-full bg-[rgba(247,147,30,0.45)] rounded-xl">
                <div class="flex items-center gap-5">
                    <i class="pi pi-info-circle text-white dark:text-black text-2xl"></i>
                    <span class="font-bold text-base text-white dark:text-black">{{ message.summary }}</span>
                </div>
                <div class="flex gap-4 mb-4 justify-end">
                    <Button label="Close" size="small" severity="contrast" @click="closeCallback"></Button>
                </div>
            </section>
        </template>
    </Toast>
    <Toast position="top-center" group="auto" @close="visible = false" />
</template>
